import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import SVGThumbsUp from '../../assets/svg/icons/SVGThumbsUp';
import SVGThumbsDown from '../../assets/svg/icons/SVGThumbsDown';
import Button, { ButtonStyle } from '../Button/Button';
import { getStorageItemForNetwork, setStorageItemForNetwork, StorageKey } from '../../../utils/storage';
import { UserFeedbackOptions } from '../../../services/authentication';
import { MetadataContext } from '../../../providers/MetadataContext';

export interface UserFeedbackProps {
  onUserFeedbackInProgress?: (sending: boolean) => void;
}

export default function UserFeedback({ onUserFeedbackInProgress }: UserFeedbackProps): JSX.Element {
  const { networkAddress, initialized } = useContext(FlowConfigContext);
  const { submitUserFeedback } = useContext(MetadataContext);
  const { t } = useTranslation();
  const [showUserFeedback, setShowUserFeedback] = useState<boolean>(false);
  const [feedbackComplete, setFeedbackComplete] = useState<boolean>(false);
  const [showPositiveButton, setShowPositiveButton] = useState<boolean>(true);
  const [showNegativeButton, setShowNegativeButton] = useState<boolean>(true);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    const shouldShowFeedback = getStorageItemForNetwork(StorageKey.UserFeedbackComplete, networkAddress) !== 'true';
    setShowUserFeedback(shouldShowFeedback);
  }, [initialized]);

  const submitAndStoreUserFeedback = async (feedback: UserFeedbackOptions) => {
    onUserFeedbackInProgress?.(true);
    setStorageItemForNetwork(StorageKey.UserFeedbackComplete, networkAddress, 'true');
    const userFeedback = submitUserFeedback(feedback);
    const timer = new Promise((resolve) => setTimeout(resolve, 1500));
    Promise.all([userFeedback, timer]).then(() => {
      onUserFeedbackInProgress?.(false);
    });
  };

  const handlePositiveButtonClick = async () => {
    setFeedbackComplete(true);
    setShowNegativeButton(false);
    await submitAndStoreUserFeedback('positive');
  };

  const handleNegativeButtonClick = async () => {
    setFeedbackComplete(true);
    setShowPositiveButton(false);
    await submitAndStoreUserFeedback('negative');
  };

  return (
    <>
      {showUserFeedback && (
        <div className="flex-shrink-0 w-2/3 flex-row justify-center items-center px-4">
          <div className="bg-primary h-px w-full mb-4" />
          <div className="my-2 text-center text-xl text-textTertiary" data-testid="USER_FEEDBACK_MESSAGE">
            {t('screens.userReview.title')}
          </div>
          <div className="flex-shrink-0 flex-grow-0 flex p-2 items-center justify-center content-between">
            {showPositiveButton && (
              <Button
                style={ButtonStyle.ROUND_OUTLINE}
                disabled={feedbackComplete}
                loading={false}
                dataTestid="USER_FEEDBACK_POSITIVE_BUTTON"
                onClick={handlePositiveButtonClick}
              >
                <SVGThumbsUp />
              </Button>
            )}

            {showPositiveButton && showNegativeButton && <div className="mx-2" />}

            {showNegativeButton && (
              <Button
                style={ButtonStyle.ROUND_OUTLINE}
                disabled={feedbackComplete}
                loading={false}
                dataTestid="USER_FEEDBACK_NEGATIVE_BUTTON"
                onClick={handleNegativeButtonClick}
              >
                <SVGThumbsDown />
              </Button>
            )}
          </div>

          {feedbackComplete ? (
            <div
              className="text-textParagraph text-lg leading-6 mt-2 mb-4 h-2 text-center"
              data-testid="USER_FEEDBACK_COMPLETE_DESCRIPTION"
            >
              {t('screens.userReview.thankYouTitle')}
            </div>
          ) : (
            <div className="mt-2 mb-4 h-2" />
          )}
        </div>
      )}
    </>
  );
}

UserFeedback.defaultProps = {
  onUserFeedbackInProgress: undefined,
};
